import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import LazyLoad from 'react-lazyload';
import { allListings, listingTypes } from '../data/listings';
import { useModal } from '../context/ModalContext';
import { generateStructuredData } from '../utils/seoUtils';
import heroBandera from '../assets/videos/hero-1-bandera.mp4';
import heroClinica from '../assets/videos/hero-2-clinica.mp4';
import heroSpa from '../assets/videos/hero-3-spa.mp4';

const HomePage = () => {
    const { openContactModal } = useModal();
    const [filteredListings, setFilteredListings] = useState([]);
    const [selectedType, setSelectedType] = useState('all');
    const [selectedProvince, setSelectedProvince] = useState('all');
    const [selectedSpecialty, setSelectedSpecialty] = useState('all');

    // Video carousel state
    const heroVideos = [heroBandera, heroClinica, heroSpa];
    const [activeVideo, setActiveVideo] = useState(0);
    const videoRefs = useRef([]);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveVideo(prev => (prev + 1) % heroVideos.length);
        }, 9000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const vid = videoRefs.current[activeVideo];
        if (vid) {
            vid.currentTime = 0;
            vid.play().catch(() => { });
        }
    }, [activeVideo]);

    // Unique provinces and specialties
    const allProvinces = [...new Set(allListings.map(listing => listing.province))].sort();
    const allSpecialties = [...new Set(allListings.flatMap(listing => listing.specialties || []))].sort();

    useEffect(() => {
        let currentListings = allListings;

        if (selectedType !== 'all') {
            currentListings = currentListings.filter(listing => listing.listingType === selectedType);
        }
        if (selectedProvince !== 'all') {
            currentListings = currentListings.filter(listing => listing.province === selectedProvince);
        }
        if (selectedSpecialty !== 'all') {
            currentListings = currentListings.filter(listing =>
                listing.specialties && listing.specialties.includes(selectedSpecialty)
            );
        }

        currentListings.sort((a, b) => {
            if (a.isFeatured && !b.isFeatured) return -1;
            if (!a.isFeatured && b.isFeatured) return 1;
            if (a.tier !== b.tier) return a.tier - b.tier;
            if (a.rating !== b.rating) return b.rating - a.rating;
            return a.name.localeCompare(b.name);
        });

        setFilteredListings(currentListings);
    }, [selectedType, selectedProvince, selectedSpecialty]);

    const renderListingCard = (listing) => {
        const isTier1 = listing.tier === 1;

        return (
            <article
                key={listing.id}
                className="premium-listing-card group"
                itemScope
                itemType="https://schema.org/MedicalBusiness"
            >
                {/* Image Placeholder with Gradient Overlay */}
                <div className="relative h-48 bg-slate-100 overflow-hidden">
                    <img
                        src={listing.images?.[0] || 'https://images.unsplash.com/photo-1560750588-73207b1ef5b8?auto=format&fit=crop&w=800&q=80'}
                        alt={listing.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    {
                        listing.isFeatured && (
                            <div className="absolute top-4 right-4 z-10">
                                <span className="badge-tier-1">★ Destacado</span>
                            </div>
                        )
                    }
                </div >

                <div className="p-6">
                    <div className="flex justify-between items-start mb-2">
                        <Link to={`/centro/${listing.id}`}>
                            <h2 className="text-xl font-bold text-slate-900 group-hover:text-rose-600 transition-colors line-clamp-1" itemProp="name">
                                {listing.name}
                            </h2>
                        </Link>
                    </div>

                    <div className="flex items-center text-sm text-slate-500 mb-4" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                        <svg className="w-4 h-4 mr-1 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span itemProp="addressLocality">{listing.city}</span>, <span itemProp="addressRegion" className="ml-1">{listing.province}</span>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                        {listing.specialties?.slice(0, 2).map((specialty, index) => (
                            <span key={index} className="text-[10px] px-2 py-1 bg-slate-100 text-slate-700 rounded-md font-bold uppercase tracking-wider border border-slate-200">
                                {specialty}
                            </span>
                        ))}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                        <div className="flex items-center">
                            <div className="flex text-amber-400">
                                {[...Array(5)].map((_, i) => (
                                    <svg key={i} className={`w-4 h-4 ${i < Math.floor(listing.rating) ? 'fill-current' : 'text-slate-200'}`} viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                ))}
                            </div>
                            <span className="ml-2 text-xs font-bold text-slate-700">{listing.rating}</span>
                        </div>
                        <Link
                            to={`/centro/${listing.id}`}
                            className="text-sm font-bold text-rose-600 hover:text-rose-700 transition-colors flex items-center"
                        >
                            Ver más
                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </article >
        );
    };

    return (
        <>
            <Helmet>
                <title>Estéticas de Argentina - Directorio Premium de Centros de Belleza</title>
                <meta name="description" content="Descubre los centros de estética más exclusivos de Argentina. Medicina estética, spas de lujo y salones verificados con tecnología de vanguardia." />
                <link rel="canonical" href="https://esteticasdeargentina.com/" />
                <script type="application/ld+json">
                    {JSON.stringify(generateStructuredData.homepage(filteredListings))}
                </script>
            </Helmet>

            {/* Hero Section */}
            <header className="relative pt-20 pb-16 overflow-hidden">
                {/* Video Carousel Background */}
                <div className="absolute inset-0 z-0">
                    {heroVideos.map((src, i) => (
                        <video
                            key={i}
                            ref={el => videoRefs.current[i] = el}
                            src={src}
                            autoPlay={i === 0}
                            muted
                            playsInline
                            loop={false}
                            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
                            style={{ opacity: activeVideo === i ? 1 : 0 }}
                        />
                    ))}
                    {/* Dark overlay for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60" />
                </div>
                <div className="container mx-auto px-6 relative z-10 text-center">
                    {/* Glass text panel */}
                    <div className="max-w-4xl mx-auto bg-black/20 backdrop-blur-sm rounded-3xl px-8 py-10 mb-6 border border-white/10 shadow-2xl">
                        <div className="inline-flex items-center bg-white/10 backdrop-blur-md text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-8 animate-float border border-white/20">
                            <span className="flex h-2 w-2 rounded-full bg-rose-400 mr-2"></span>
                            Directorio 2026 Activo
                        </div>
                        <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight text-white drop-shadow-lg">
                            La excelencia en <br />
                            <span className="text-gradient">Estética Argentina</span>
                        </h1>
                        <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto leading-relaxed">
                            Conectamos a pacientes exigentes con los centros y profesionales más destacados del país. Tu transformación comienza aquí.
                        </p>

                        {/* Carousel dot indicators */}
                        <div className="flex justify-center gap-2">
                            {heroVideos.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveVideo(i)}
                                    className={`h-1.5 rounded-full transition-all duration-500 ${activeVideo === i ? 'w-8 bg-white' : 'w-2 bg-white/40'
                                        }`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Filter Glassmorphism Box */}
                    <div className="max-w-5xl mx-auto mt-4">
                        <div className="glass-card p-2 md:p-3 rounded-[2rem] shadow-2xl shadow-rose-500/10 border border-white/40">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:divide-x divide-slate-100">
                                {/* Especialidad */}
                                <div className="relative group px-4 py-3">
                                    <div className="flex items-center mb-1 space-x-2">
                                        <svg className="w-3 h-3 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        <label className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.15em]">Especialidad</label>
                                    </div>
                                    <select
                                        value={selectedSpecialty}
                                        onChange={(e) => setSelectedSpecialty(e.target.value)}
                                        className="w-full bg-transparent border-none p-0 focus:ring-0 cursor-pointer text-slate-900 font-bold text-sm appearance-none"
                                    >
                                        <option value="all">Todas las especialidades</option>
                                        {allSpecialties.map(specialty => <option key={specialty} value={specialty}>{specialty}</option>)}
                                    </select>
                                </div>

                                {/* Provincia */}
                                <div className="relative group px-4 py-3">
                                    <div className="flex items-center mb-1 space-x-2">
                                        <svg className="w-3 h-3 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        </svg>
                                        <label className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.15em]">Provincia</label>
                                    </div>
                                    <select
                                        value={selectedProvince}
                                        onChange={(e) => setSelectedProvince(e.target.value)}
                                        className="w-full bg-transparent border-none p-0 focus:ring-0 cursor-pointer text-slate-900 font-bold text-sm appearance-none"
                                    >
                                        <option value="all">Todas las provincias</option>
                                        {allProvinces.map(province => <option key={province} value={province}>{province}</option>)}
                                    </select>
                                </div>

                                {/* Tipo */}
                                <div className="relative group px-4 py-3">
                                    <div className="flex items-center mb-1 space-x-2">
                                        <svg className="w-3 h-3 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                        </svg>
                                        <label className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.15em]">Nivel de Centro</label>
                                    </div>
                                    <select
                                        value={selectedType}
                                        onChange={(e) => setSelectedType(e.target.value)}
                                        className="w-full bg-transparent border-none p-0 focus:ring-0 cursor-pointer text-slate-900 font-bold text-sm appearance-none"
                                    >
                                        <option value="all">Todos los estilos</option>
                                        {Object.entries(listingTypes).map(([key, val]) => <option key={key} value={key}>{val}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Decorative blobs */}
                <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-96 h-96 bg-rose-200/20 rounded-full blur-3xl opacity-50" />
                <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-96 h-96 bg-violet-200/20 rounded-full blur-3xl opacity-50" />
            </header>

            <main className="container mx-auto px-6 py-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-12">
                    <div>
                        <h2 className="text-3xl font-bold text-slate-900 mb-2">Explorar Resultados</h2>
                        <p className="text-slate-500">Mostrando {filteredListings.length} centros de estética verificados</p>
                    </div>
                    <div className="mt-4 md:mt-0 flex gap-2">
                        <button className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-slate-600 hover:border-rose-200 transition-all">
                            Más populares
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {filteredListings.length > 0 ? (
                        filteredListings.map(listing => (
                            <LazyLoad key={listing.id} height={400} offset={200}>
                                {renderListingCard(listing)}
                            </LazyLoad>
                        ))
                    ) : (
                        <div className="col-span-full py-20 text-center glass-card">
                            <div className="text-5xl mb-4">🔍</div>
                            <h3 className="text-xl font-bold mb-2">No encontramos resultados</h3>
                            <p className="text-slate-500">Intenta ajustar los filtros para encontrar lo que buscas.</p>
                            <button
                                onClick={() => { setSelectedProvince('all'); setSelectedType('all'); setSelectedSpecialty('all'); }}
                                className="mt-6 text-rose-600 font-bold hover:underline"
                            >
                                Limpiar filtros
                            </button>
                        </div>
                    )}
                </div>
            </main>

            {/* CTA Section */}
            <section className="container mx-auto px-6 py-20">
                <div className="bg-slate-900 rounded-[3rem] p-12 relative overflow-hidden">
                    <div className="relative z-10 max-w-2xl">
                        <h2 className="text-4xl font-bold text-white mb-6">¿Eres dueño de un centro de estética?</h2>
                        <p className="text-slate-400 text-lg mb-8 uppercase tracking-wide font-medium">Llega a miles de pacientes que buscan tus servicios.</p>
                        <button
                            onClick={openContactModal}
                            className="btn-premium-primary"
                        >
                            <span>Sumar mi centro ahora</span>
                            <svg className="w-5 h-5 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </button>
                    </div>
                    {/* Abstract background element */}
                    <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-rose-500/20 to-transparent skew-x-12 translate-x-1/4" />
                </div>
            </section>
        </>
    );
};

export default HomePage;